import { useMemo } from "react";
import type {
  CyFunRequirement,
  CyFunSubcategory,
  MaturityEvaluationRubric,
} from "@/types/maturity";
import { RequirementScoreRow } from "../RequirementScoreRow";

type FullEvalSubcategoryCardProps = {
  subcategory: CyFunSubcategory;
  requirements: CyFunRequirement[];
  keyMeasureCodes: string[];
  responses: Record<string, number>;
  notes: Record<string, string>;
  rubricsByCode: Map<string, MaturityEvaluationRubric[]>;
  onScoreChange: (key: string, value: number) => void;
  onNoteChange: (key: string, value: string) => void;
  onNoteBlur: () => void;
};

function FullEvalSubcategoryCard({
  subcategory,
  requirements,
  keyMeasureCodes,
  responses,
  notes,
  rubricsByCode,
  onScoreChange,
  onNoteChange,
  onNoteBlur,
}: FullEvalSubcategoryCardProps) {
  const answeredCount = useMemo(() => {
    return requirements.filter(
      (req) =>
        responses[`${req.code}-D`] !== undefined &&
        responses[`${req.code}-I`] !== undefined
    ).length;
  }, [requirements, responses]);

  const totalCount = requirements.length;
  const isComplete = answeredCount === totalCount && totalCount > 0;

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-semibold text-slate-400">
            {subcategory.code}
          </span>
          <span className="text-sm font-medium text-slate-700">
            {subcategory.name}
          </span>
        </div>
        <span
          className={`text-xs font-medium font-mono px-2 py-0.5 rounded-full ${
            isComplete
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {answeredCount}/{totalCount}
        </span>
      </div>

      <div className="px-4">
        {requirements.map((req) => (
          <RequirementScoreRow
            key={req.code}
            requirement={req}
            isKeyMeasure={keyMeasureCodes.includes(req.code)}
            docScore={responses[`${req.code}-D`]}
            implScore={responses[`${req.code}-I`]}
            docNote={notes[`${req.code}-D`] ?? ""}
            implNote={notes[`${req.code}-I`] ?? ""}
            rubrics={rubricsByCode.get(req.code) ?? []}
            onScoreChange={onScoreChange}
            onNoteChange={onNoteChange}
            onNoteBlur={onNoteBlur}
          />
        ))}
      </div>
    </div>
  );
}

export default FullEvalSubcategoryCard;
